import { createStyles, makeStyles, Theme } from '@material-ui/core';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import React from 'react';

import TnButton from '../../../../_components/tn-button/TnButton';
import TnTypography from '../../../../_components/tn-typography/TnTypography';

import EmploymentForm, { Employment } from './EmploymentForm';
import EmploymentPosition from './EmploymentPosition';

interface EmploymentHandlers {
  onAddEmployment: (employment: Employment) => void;
  onEditEmployment: (index: number, employment: Employment) => void;
  onDeleteEmployment: (index: number) => void;
}

interface Props {
  employmentHistory: Employment[];
  changeHandlers: EmploymentHandlers;
  onFormOpenChange?: (isOpen: boolean) => void;
  onFormValidityChange?: (isValid: boolean, invalidFields: string[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    labelContainer: {
      width: '100%',
      marginBottom: 16,
    },
    addEmploymentButton: {
      width: 200,
    },
    filledOutHistoryContainer: {
      width: 'calc(100% - 32px)',
      backgroundColor: '#FAFAFA',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      border: '1px solid #E0E0E0',
      padding: '16px',
      gridGap: 8,
      borderRadius: 4,
      justifyContent: 'flex-start',
      marginBottom: 16,
      '&:not(:last-child)': {
        borderBottom: `1px solid #E0E0E0`,
      },
    },
  }),
);

export default function EmploymentHistory({
  employmentHistory,
  changeHandlers,
  onFormOpenChange,
  onFormValidityChange,
}: Readonly<Props>) {
  const classes = useStyles();

  const [newEmploymentFormOpen, setNewEmploymentFormOpen] = React.useState(false);

  const handleFormOpenChange = React.useCallback(
    (isOpen: boolean) => {
      onFormOpenChange?.(isOpen);
    },
    [onFormOpenChange],
  );

  const sortedEmploymentHistory = employmentHistory.sort((a, b) => {
    const aEndDate = a.endDate || new Date().toISOString();
    const bEndDate = b.endDate || new Date().toISOString();

    return new Date(bEndDate).getTime() - new Date(aEndDate).getTime();
  });

  return (
    <>
      <div className={classes.labelContainer}>
        <TnTypography bold variant="body">
          Experience
        </TnTypography>
      </div>
      <div className={classes.labelContainer}>
        <TnTypography variant="caption">List previous relevant work experience</TnTypography>
      </div>
      {sortedEmploymentHistory.length > 0 && (
        <div className={classes.filledOutHistoryContainer}>
          {sortedEmploymentHistory.map((employment, index) => (
            <EmploymentPosition
              key={employment.companyName}
              employment={employment}
              onFormOpenChange={handleFormOpenChange}
              onFormValidityChange={onFormValidityChange}
              changeHandlers={{
                onEditEmployment: (updatedEmployment) => changeHandlers.onEditEmployment(index, updatedEmployment),
                onDeleteEmployment: () => changeHandlers.onDeleteEmployment(index),
              }}
            />
          ))}
        </div>
      )}
      {newEmploymentFormOpen && (
        <EmploymentForm
          mode="create"
          onCancel={() => {
            setNewEmploymentFormOpen(false);
            handleFormOpenChange(false);
          }}
          onSubmit={(employment) => {
            changeHandlers.onAddEmployment(employment);
            setNewEmploymentFormOpen(false);
            handleFormOpenChange(false);
          }}
          onValidityChange={onFormValidityChange}
        />
      )}
      <div className={classes.addEmploymentButton}>
        <TnButton
          startIcon={<AddCircleOutline />}
          value="Add Experience"
          variant="outlined"
          color="secondary"
          disabled={newEmploymentFormOpen}
          onClick={() => {
            setNewEmploymentFormOpen(true);
            handleFormOpenChange(true);
          }}
        />
      </div>
    </>
  );
}
